import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { IconHeart } from '../../../../../assets/icons/icon-heart';
import { IconWarning } from '../../../../../assets/icons/icon-warning';
import { Carousel } from '../../../../../components/carousel/carousel';
import { CarouselItem } from '../../../../../components/carousel/carousel-item';
import { EmptyStateBox } from '../../../../../components/empty-state-box';
import { useInstallationContext } from '../../../../../context/installation';
import { UserGroup, UserPermissionType } from '../../../../../data-access/gql-types/graphql';
import { useUserPermissions } from '../../../../../hooks/user/use-user-permissions';
import { ROUTES } from '../../../../../routes/routes';
import { useGroups } from '../../../hooks/groups/use-groups';
import { useGroupsStore } from '../../../store/groups';
import { GroupsItems } from './items';

type GroupsCarouselProps = {
  activeTabIndex: number;
  setActiveTabIndex: (value: number) => void;
};

export const GroupsCarousel: React.FC<GroupsCarouselProps> = ({ activeTabIndex, setActiveTabIndex }) => {
  const { t: tg } = useTranslation('groups');
  const { groupsLoading, groups } = useGroups();
  const { installationsLoading } = useInstallationContext();
  const { permissions } = useUserPermissions();
  const isListView = useGroupsStore((s) => s.isListView);

  const canEdit = (userGroup: UserGroup) =>
    (userGroup.group.isPublicGroup && permissions[UserPermissionType.PublicGroupEdit]) ||
    (!userGroup.group.isPublicGroup && permissions[UserPermissionType.GroupEdit]);

  return (
    <>
      {!groupsLoading && !installationsLoading && (
        <Carousel setActiveIndex={setActiveTabIndex} activeIndex={activeTabIndex}>
          {groups?.map((userGroup) => {
            return (
              <CarouselItem key={userGroup.id} className={'m-t-8'}>
                {userGroup.userGroupItems.length === 0 && (
                  <>
                    {userGroup.group.isDefaultGroup ? (
                      <EmptyStateBox
                        header={tg('favourites.header')}
                        btnText={tg('favourites.add')}
                        content={tg('favourites.emptyState')}
                        icon={<IconHeart isEmpty />}
                        linkTo={ROUTES.FavouriteGroupEdit(userGroup.id)}
                      />
                    ) : (
                      <EmptyStateBox
                        header={tg('custom.header')}
                        btnText={tg('custom.add')}
                        content={tg(`custom.${canEdit(userGroup) ? 'emptyState' : 'emptyStateNoPermission'}`)}
                        icon={<IconWarning />}
                        {...(canEdit(userGroup) ? { linkTo: ROUTES.GroupEdit(userGroup.id) } : {})}
                      />
                    )}
                  </>
                )}
                {userGroup.userGroupItems.length > 0 && (
                  <div
                    className={classNames('p-b-24', {
                      'grid grid--2 grid--small-gap': !isListView,
                      'grid-list-16': isListView,
                    })}
                  >
                    <GroupsItems group={userGroup} isListView={isListView} />
                  </div>
                )}
              </CarouselItem>
            );
          })}
        </Carousel>
      )}
    </>
  );
};
